import type { Locale } from '@iac/contracts';
import { useState } from 'react';
import type { Dictionary } from '../i18n/dictionary';
import type { Story } from '../lib/story';
import { FilterChips } from './FilterChips';

const sample: Dictionary<{ label: string; tags: string[]; many: string[] }> = {
  pl: {
    label: 'Tagi',
    tags: ['obiad', 'szybkie', 'wege', 'zupa'],
    many: [
      'ciasto',
      'deser',
      'grill',
      'kolacja',
      'makaron',
      'obiad',
      'przekąska',
      'ryby',
      'sałatka',
      'szybkie',
      'śniadanie',
      'święta',
      'wege',
      'zupa',
    ],
  },
  en: {
    label: 'Tags',
    tags: ['dinner', 'quick', 'soup', 'veggie'],
    many: [
      'baking',
      'breakfast',
      'dessert',
      'dinner',
      'fish',
      'grill',
      'holidays',
      'lunch',
      'pasta',
      'quick',
      'salad',
      'snack',
      'soup',
      'veggie',
    ],
  },
};

/** The chips own no state; the screen around them does. */
function Demo({
  options,
  initial,
  label,
}: {
  options: string[];
  initial: string[];
  label: string;
}) {
  const [selected, setSelected] = useState(initial);
  return (
    <FilterChips
      label={label}
      options={options}
      selected={selected}
      onChange={setSelected}
    />
  );
}

const variant = (locale: Locale, pick: (tags: string[]) => string[]) => {
  const t = sample[locale];
  return <Demo label={t.label} options={t.tags} initial={pick(t.tags)} />;
};

export default {
  kind: 'component',
  title: { pl: 'Chipy filtra', en: 'Filter chips' },
  description: {
    pl: 'Grupa przełączanych przycisków, która zawęża listę do wybranych wartości, np. tagów. Stan wyboru trzyma ekran; komponent zgłasza tylko nowy wybór. Każdy chip ma `aria-pressed`.',
    en: 'A group of toggle buttons that narrows a list to the picked values, such as tags. The screen owns the selection; the component only reports the next one. Each chip carries `aria-pressed`.',
  },
  variants: [
    {
      name: { pl: 'Nic nie wybrano', en: 'Nothing selected' },
      render: (locale) => variant(locale, () => []),
    },
    {
      name: { pl: 'Część wybrana', en: 'Some selected' },
      render: (locale) => variant(locale, (tags) => tags.slice(1, 3)),
    },
    {
      name: { pl: 'Zawijanie', en: 'Wrapping' },
      render: (locale) => (
        <Demo
          label={sample[locale].label}
          options={sample[locale].many}
          initial={[]}
        />
      ),
    },
  ],
} satisfies Story;
